// UC20 — the combined medicine nomenclator registry (Administrator Sistem).
// per docs/shape-uc-20-medicine-nomenclator.md §5
// The table is AGE's mud-table with every column the wireframe lists (fifteen,
// well past the six the MUD table is drawn for) and the row actions in a leading
// kebab column: an owner requirement stated in
// wireframes/react/src/pages/AdminCoduri.tsx. It is wider than any viewport by
// design and scrolls sideways inside mud-table's own scroll region; the page
// body never scrolls.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCriteriaParams } from '../../app/use-criteria-params';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudIcon,
  MudInfoBox,
  MudModal,
  MudToast,
  MudSelect,
} from '@egov-moldova/mud-react';
import { apiClient, apiErrorKey } from '../../app/api-client';
import {
  FilterBar,
  PageHeader,
  RowActionsMenu,
  formatDateTime,
  onModalDismiss,
  type ActiveFilter,
} from '@simsm/ui-shared';
import { MedicinesRegister } from './MedicinesRegister';
import { usePageSize } from '../../components/TableFooter/TableFooter';
import {
  fetchMedicinePage,
  ATC_GROUPS,
  MEDICINE_CODE_KINDS,
  MEDICINE_EXPIRIES,
  MEDICINE_STATUSES,
  MEDICINE_SOURCES,
  type MedicineCodeKind,
  type MedicineSource,
} from './medicines-api';
import './medicines.css';

// UC20-03/04 configure the two feeds separately, so UC20-05 can sync them
// separately: the header's «Sincronizează tot» (CF-93, T175) runs both in one
// request, and the secondary menu beside it, like the dialog's rows, runs one.
const NOMENCLATOR_TYPES = ['autorizat', 'neautorizat'] as const;
type NomenclatorType = (typeof NOMENCLATOR_TYPES)[number];
// What the combined action sends as `nomenclatorType`: one cycle over both feeds.
const ALL_NOMENCLATORS = 'all';
type SyncScope = NomenclatorType | typeof ALL_NOMENCLATORS;

export interface SyncFeedResult {
  nomenclatorType: string;
  status: string;
  receivedCount: number;
  addedCount: number;
  updatedCount: number;
  inactivatedCount: number;
  errorCount: number;
  error: string | null;
}

interface SyncResult {
  syncedAtIso: string;
  addedCount: number;
  updatedCount: number;
  inactivatedCount: number;
  staleMarkingRan: boolean;
  perType: SyncFeedResult[];
}

interface SyncFeedStatus {
  nomenclatorType: string;
  status: string | null;
  startedAtIso: string | null;
  finishedAtIso: string | null;
  itemsRead: number;
  itemsChanged: number;
  error: string | null;
  trigger: string | null;
}

interface SyncStatus {
  lastSyncIso: string | null;
  perType: SyncFeedStatus[];
}

interface SyncMessage {
  tone: 'success' | 'error';
  text: string;
}

/**
 * What the run actually did, which is not always what was asked for. A feed that
 * failed is named with its reason. Every run reports its retirements, since each
 * type retires what its own list dropped (docs/shape-uc-20-source-wins.md §2.3), so
 * both messages carry the inactivated count. A run of both types reports the cycle's
 * totals; a run of one names its feed (TC-UC20-05-01;
 * docs/shape-ops-nomenclator-sync-audit.md §D7). `staleMarkingRan` is now true after
 * every run and no longer tells the two apart.
 */
export function describeSyncResult(
  result: SyncResult,
  label: (nomenclatorType: string) => string,
): { tone: 'success' | 'error'; key: string; params: Record<string, string | number> } {
  const failed = result.perType.find((feed) => feed.status === 'FAILED');
  if (failed) {
    return {
      tone: 'error',
      key: 'medicines.sync.failed',
      params: { type: label(failed.nomenclatorType), reason: failed.error ?? '' },
    };
  }
  const added = result.perType.reduce((sum, feed) => sum + feed.addedCount, 0);
  const updated = result.perType.reduce((sum, feed) => sum + feed.updatedCount, 0);
  if (result.perType.length > 1) {
    return {
      tone: 'success',
      key: 'medicines.sync.success',
      params: { added, updated, inactivated: result.inactivatedCount },
    };
  }
  // TC-UC20-05-01; docs/shape-ops-nomenclator-sync-audit.md §D7.
  return {
    tone: 'success',
    key: 'medicines.sync.success_feed',
    params: {
      type: result.perType.map((feed) => label(feed.nomenclatorType)).join(', '),
      added,
      updated,
      inactivated: result.inactivatedCount,
    },
  };
}

/**
 * What one feed did in a run, for the per-type list the dialog shows after
 * «Sincronizează tot» (CF-93): its received/added/updated/inactivated counts, or the
 * reason it failed. A feed that failed in the same run as one that succeeded is still
 * named here, which the single banner of `describeSyncResult` cannot do. The feed's
 * inactivated count is what its own source stopped listing; the banner's cycle total
 * also holds the expiry sweep, so the lines need not add up to it.
 */
export function describeFeedOutcome(
  feed: SyncFeedResult,
  label: (nomenclatorType: string) => string,
): { failed: boolean; key: string; params: Record<string, string | number> } {
  if (feed.status === 'FAILED') {
    return {
      failed: true,
      key: 'medicines.sync.outcome.failed',
      params: { type: label(feed.nomenclatorType), reason: feed.error ?? '' },
    };
  }
  return {
    failed: false,
    key: 'medicines.sync.outcome.done',
    params: {
      type: label(feed.nomenclatorType),
      received: feed.receivedCount,
      added: feed.addedCount,
      updated: feed.updatedCount,
      inactivated: feed.inactivatedCount,
    },
  };
}

function oneOf<T extends string>(allowed: readonly T[], value: string, fallback: T): T {
  return (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

function isSource(value: string): value is MedicineSource {
  return (MEDICINE_SOURCES as string[]).includes(value);
}

export function MedicinesPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // ACCS-13: the criteria live in the URL, so a filtered registry is a link.
  const [searchParams, setSearchParams] = useCriteriaParams();
  const [syncMessage, setSyncMessage] = useState<SyncMessage | null>(null);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncingType, setSyncingType] = useState<SyncScope | null>(null);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);

  const q = searchParams.get('q') ?? '';
  // The position just added (TC-UC20-08-01): the editor sends the administrator back here
  // with the new code in the search box and this marking the row. Any later change to the
  // criteria drops it, because the list it pointed into is no longer the one on screen.
  const highlightId = searchParams.get('highlight') ?? undefined;
  // Five independent criteria, each its own URL parameter. `scope` used to carry the
  // authorisation kind and the status in one control, which made status look like a
  // third kind and made "the inactive AUTHORISED codes" — the set UC20-09 and UC20-10
  // produce — impossible to ask for at all.
  const kind = oneOf(MEDICINE_CODE_KINDS, searchParams.get('kind') ?? '', '' as never) as
    | MedicineCodeKind
    | '';
  const status = oneOf(MEDICINE_STATUSES, searchParams.get('status') ?? 'all', 'all');
  const expiry = oneOf(MEDICINE_EXPIRIES, searchParams.get('expiry') ?? 'all', 'all');
  const atcGroup = oneOf(ATC_GROUPS, searchParams.get('atc') ?? '', '' as never) as string;
  const sourceParam = searchParams.get('source') ?? '';
  const source: MedicineSource | '' = isSource(sourceParam) ? sourceParam : '';
  const page = Math.max(1, Number.parseInt(searchParams.get('p') ?? '1', 10) || 1);
  const [pageSize, setPageSize] = usePageSize('medicines');
  const offset = (page - 1) * pageSize;
  // Which grain the register is read at. In the URL like every other criterion
  // (ACCS-13), so a colleague opening the link sees the same list.

  function setParams(changes: Record<string, string>, resetPage = true): void {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    if (resetPage) {
      next.delete('p');
      next.delete('highlight');
    }
    setSearchParams(next);
  }

  // One register, one grain: a row is a MEDICINE, with its codes in a column (owner
  // ruling Q-1-13, 2026-10-13: like the wireframe; TC-UC20-08-01). It replaces the
  // one-row-per-code register of 2026-09-10.
  const medicines = useQuery({
    queryKey: ['admin-medicines', q, kind, status, expiry, atcGroup, source, offset, pageSize],
    queryFn: () =>
      fetchMedicinePage({
        q,
        kind,
        status,
        expiry,
        atcGroup,
        source,
        limit: pageSize,
        offset,
      }),
  });
  const syncStatus = useQuery({
    queryKey: ['admin-medicines-sync'],
    queryFn: async () => {
      const { data } = await apiClient.get<SyncStatus>('/admin/medicines/sync-status');
      return data;
    },
  });

  const sync = useMutation({
    mutationFn: async (nomenclatorType: SyncScope) => {
      const { data } = await apiClient.post<SyncResult>('/admin/medicines/sync', {
        nomenclatorType,
      });
      return data;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['admin-medicines'] });
      queryClient.invalidateQueries({ queryKey: ['admin-medicines-sync'] });
      const described = describeSyncResult(result, (type) => t(`system_config.type.${type}`));
      setSyncResult(result);
      setSyncMessage({ tone: described.tone, text: t(described.key, described.params) });
    },
    // The fallback names this operation: the feed being unreachable is what a
    // sync fails on when the service sends no code we recognise.
    onError: (err) =>
      setSyncMessage({ tone: 'error', text: t(apiErrorKey(err, 'medicines.sync.unreachable')) }),
    onSettled: () => setSyncingType(null),
  });

  // One entry point for the header action, the secondary menu and the dialog's
  // rows: the dialog is where the run's outcome is shown, so it opens with the run.
  function runSync(scope: SyncScope): void {
    setSyncMessage(null);
    setSyncResult(null);
    setSyncingType(scope);
    setSyncOpen(true);
    sync.mutate(scope);
  }

  // No page-level "last synchronised" line. The wireframe
  // (wireframes/react/src/pages/AdminCoduri.tsx:330) gives this header a title and
  // one lede, and puts synchronisation where it can be acted on: per feed inside
  // the "Sincronizează" dialog, and per position in the "Ultima modificare"
  // column. An aggregate third line under the title says less than either and
  // pushed the register down a row on every load.
  //
  // One line per feed, never borrowed from the other: a weekly nomenclator six
  // days idle must not read as synced this morning because the daily one was.
  function feedStatusLine(type: NomenclatorType): string {
    const feed = syncStatus.data?.perType.find((row) => row.nomenclatorType === type);
    if (!feed?.status) {
      return t('medicines.sync.feed.never');
    }
    const stamp = formatDateTime(feed.finishedAtIso ?? feed.startedAtIso ?? '');
    return feed.status === 'FAILED'
      ? t('medicines.sync.feed.failed', { timestamp: stamp, reason: feed.error ?? '' })
      : t('medicines.sync.feed.done', {
          timestamp: stamp,
          read: feed.itemsRead,
          changed: feed.itemsChanged,
        });
  }

  // The search term gets no chip: it clears from the box's own ×, which is the
  // wireframe's own rule for the shared bar (BaraFiltre.tsx:35).
  const activeFilters: ActiveFilter[] = [
    ...(kind
      ? [
          {
            key: 'kind',
            label: t('medicines.filter.kind'),
            value: t(`medicines.kind.${kind}`),
            onRemove: () => setParams({ kind: '' }),
          },
        ]
      : []),
    ...(status !== 'all'
      ? [
          {
            key: 'status',
            label: t('medicines.filter.status'),
            value: t(`medicines.status.${status}`),
            onRemove: () => setParams({ status: '' }),
          },
        ]
      : []),
    ...(expiry !== 'all'
      ? [
          {
            key: 'expiry',
            label: t('medicines.filter.expiry'),
            value: t(`medicines.expiry.${expiry}`),
            onRemove: () => setParams({ expiry: '' }),
          },
        ]
      : []),
    ...(atcGroup
      ? [
          {
            key: 'atc',
            label: t('medicines.filter.atc_group'),
            value: `${atcGroup} — ${t(`medicines.atc_group.${atcGroup}`)}`,
            onRemove: () => setParams({ atc: '' }),
          },
        ]
      : []),
  ];

  return (
    <section className="simsm-page simsm-medicines">
      <PageHeader
        title={t('medicines.title')}
        lede={t('medicines.lede')}
        actions={
          <>
            <MudButton
              shape="circular"
              variant="neutral"
              size="md"
              onClick={() => navigate('/system-admin/medicines/import')}
            >
              <MudIcon slot="icon-start" name="upload" size={16} />
              {t('medicines.action.import')}
            </MudButton>
            {/* CF-93 (T175) — the one synchronisation action: both nomenclators from the
                single source in one request. The per-type run stays one menu away, so
                UC20-05 («fiecare tip de nomenclator») and CF-71 still hold. */}
            <MudButton
              shape="circular"
              variant="neutral"
              size="md"
              loading={sync.isPending && syncingType === ALL_NOMENCLATORS}
              disabled={sync.isPending}
              onClick={() => runSync(ALL_NOMENCLATORS)}
            >
              <MudIcon slot="icon-start" name="rotate-arrow" size={16} />
              {sync.isPending && syncingType === ALL_NOMENCLATORS
                ? t('medicines.action.syncing')
                : t('medicines.action.sync_all')}
            </MudButton>
            <RowActionsMenu
              label={t('medicines.action.sync_menu')}
              size="md"
              align="end"
              actions={NOMENCLATOR_TYPES.map((type) => ({
                label: t(`medicines.action.sync_only.${type}`),
                disabled: sync.isPending,
                onSelect: () => runSync(type),
              }))}
            />
            <MudButton
              shape="circular"
              variant="primary"
              size="md"
              onClick={() => navigate('/system-admin/medicines/new')}
            >
              <MudIcon slot="icon-start" name="plus-large" size={16} />
              {t('medicines.action.new')}
            </MudButton>
          </>
        }
      />

      {/* UC20-05 — the outcome of the run the header started, and one action per
          nomenclator, as the wireframe's modal has it. */}
      <MudModal
        closeLabel={t('common.close')}
        open={syncOpen}
        size="md"
        titleText={t('medicines.sync.title')}
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 —
        // seven AGE components emit `mudClose` as a bubbling, composed event, so
        // a descendant closing inside this modal reaches this handler too.
        // Without the guard the `closable` notification below takes the modal
        // down when the administrator dismisses the sync result.
        onMudClose={onModalDismiss(() => setSyncOpen(false))}
      >
        <div className="simsm-medicines__sync">
          <p className="simsm-medicines__sync-lede">{t('medicines.sync.lede')}</p>
          {/* MUD 1.2 has no success info-box, so the success outcome stays a toast held
              at the column's width by `.simsm-inline-outcome`; the failure is an info-box. */}
          {syncMessage?.tone === 'error' ? (
            <MudInfoBox
              emphasis="strong"
              role="alert"
              variant="error"
              closable
              // AGE defaults the close button's accessible name to its own
              // Romanian word for it, so a Russian or English reader hears the
              // wrong language from the only control that dismisses this banner.
              closeLabel={t('common.close')}
              onMudClose={() => setSyncMessage(null)}
            >
              {syncMessage.text}
            </MudInfoBox>
          ) : syncMessage ? (
            <div className="simsm-inline-outcome">
              <MudToast
                variant="success"
                closable
                closeLabel={t('common.close')}
                onMudClose={() => setSyncMessage(null)}
              >
                {syncMessage.text}
              </MudToast>
            </div>
          ) : null}
          {sync.isPending && syncingType === ALL_NOMENCLATORS ? (
            <p className="simsm-medicines__sync-lede" role="status">
              {t('medicines.sync.running_all')}
            </p>
          ) : null}
          {/* CF-93 — what each feed did, so a half-failed «Sincronizează tot» says which half. */}
          {syncResult && syncResult.perType.length > 0 ? (
            <ul
              className="simsm-medicines__sync-outcome"
              aria-label={t('medicines.sync.outcome.heading')}
            >
              {syncResult.perType.map((feed) => {
                const outcome = describeFeedOutcome(feed, (type) =>
                  t(`system_config.type.${type}`),
                );
                return (
                  <li
                    key={feed.nomenclatorType}
                    data-feed={feed.nomenclatorType}
                    data-outcome={outcome.failed ? 'failed' : 'done'}
                  >
                    {t(outcome.key, outcome.params)}
                  </li>
                );
              })}
            </ul>
          ) : null}
          {NOMENCLATOR_TYPES.map((type) => (
            <div key={type} className="simsm-medicines__sync-row">
              <span className="simsm-medicines__sync-name">
                <strong>{t(`system_config.type.${type}`)}</strong>
                <span className="simsm-medicines__sync-meta">{feedStatusLine(type)}</span>
              </span>
              <MudButton
                shape="circular"
                variant="neutral"
                size="md"
                loading={syncingType === type}
                disabled={sync.isPending}
                onClick={() => runSync(type)}
              >
                {syncingType === type ? t('medicines.action.syncing') : t('medicines.action.sync')}
              </MudButton>
            </div>
          ))}
        </div>
        <div slot="actions">
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => setSyncOpen(false)}
          >
            {t('common.close')}
          </MudButton>
        </div>
      </MudModal>

      <FilterBar
        search={{
          value: q,
          onChange: (value) => setParams({ q: value }),
          placeholder: t('medicines.search.placeholder'),
          ariaLabel: t('medicines.search.aria'),
          clearLabel: t('common.filter.clear_search'),
        }}
        active={activeFilters}
        onReset={() => setParams({ q: '', kind: '', status: '', expiry: '', atc: '', source: '' })}
        labels={{
          filters: t('common.filter.button'),
          reset: t('common.filter.reset'),
          panelAriaLabel: t('medicines.filter.aria_label'),
          activeAriaLabel: t('common.filter.active_aria_label'),
        }}
        info={
          // The history log is the only tool this register offers beside its own
          // actions. The comparison screen and the CSV export that used to stand
          // here are removed per specs/040-remove-nomenclator-diff: no UC-NN-XX
          // asks for either, and the screen carries what the caiet asks.
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => navigate('/system-admin/medicines/history')}
          >
            <MudIcon slot="icon-start" name="clock" size={16} />
            {t('history.action.open_log')}
          </MudButton>
        }
        panel={
          <>
            {/* Five criteria, five controls, in the order an administrator narrows:
                what it is, whether it still counts, when it lapses, which therapeutic
                family, where it came from. */}
            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">{t('medicines.filter.kind')}</span>
              <MudSelect
                size="medium"
                aria-label={t('medicines.filter.kind')}
                value={kind}
                placeholder={t('medicines.kind.any')}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setParams({ kind: e.detail.value })
                }
              >
                <option value="">{t('medicines.kind.any')}</option>
                {MEDICINE_CODE_KINDS.filter((value) => value !== 'BARCODE').map((value) => (
                  <option key={value} value={value}>
                    {t(`medicines.kind.${value}`)}
                  </option>
                ))}
              </MudSelect>
            </label>
            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">{t('medicines.filter.status')}</span>
              <MudSelect
                size="medium"
                aria-label={t('medicines.filter.status')}
                value={status}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setParams({ status: e.detail.value === 'all' ? '' : e.detail.value })
                }
              >
                {MEDICINE_STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {t(`medicines.status.${value}`)}
                  </option>
                ))}
              </MudSelect>
            </label>
            {/* The operational one. UC20-10 turns an authorised code inactive twelve
                months after it lapses, so "what is about to expire" has to be askable
                before it answers itself — 95 codes are inside 90 days today. */}
            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">{t('medicines.filter.expiry')}</span>
              <MudSelect
                size="medium"
                aria-label={t('medicines.filter.expiry')}
                value={expiry}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setParams({ expiry: e.detail.value === 'all' ? '' : e.detail.value })
                }
              >
                {MEDICINE_EXPIRIES.map((value) => (
                  <option key={value} value={value}>
                    {t(`medicines.expiry.${value}`)}
                  </option>
                ))}
              </MudSelect>
            </label>
            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">
                {t('medicines.filter.atc_group')}
              </span>
              <MudSelect
                size="medium"
                aria-label={t('medicines.filter.atc_group')}
                value={atcGroup}
                placeholder={t('medicines.atc_group.any')}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setParams({ atc: e.detail.value })
                }
              >
                <option value="">{t('medicines.atc_group.any')}</option>
                {ATC_GROUPS.map((value) => (
                  <option
                    key={value}
                    value={value}
                  >{`${value} — ${t(`medicines.atc_group.${value}`)}`}</option>
                ))}
              </MudSelect>
            </label>
            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">{t('medicines.filter.source')}</span>
              <MudSelect
                size="medium"
                aria-label={t('medicines.filter.source')}
                value={source}
                placeholder={t('medicines.source.any')}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setParams({ source: e.detail.value })
                }
              >
                <option value="">{t('medicines.source.any')}</option>
                {MEDICINE_SOURCES.map((value) => (
                  <option key={value} value={value}>
                    {t(`medicines.source.${value}`)}
                  </option>
                ))}
              </MudSelect>
            </label>
          </>
        }
      />

      <MedicinesRegister
        query={medicines}
        highlightId={highlightId}
        page={page}
        pageSize={pageSize}
        onPage={(next) => setParams({ p: String(next) }, false)}
        onPageSize={setPageSize}
      />
    </section>
  );
}
